import { formatMoneyValue } from "@/app/utils/formatMoneyValue"
import Goals from "@/view/components/Goals"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/view/components/ui/card"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/ui/avatar"
import { stringToColour } from "@/app/utils/changeBackgroundColorOfInitials"
import { getNameInitial } from "@/app/utils/getNameInitial"
import { UserRole } from "@/app/entities/UserRole"
import { formatName } from "@/app/utils/formatName"
import { useSupervisorsCardController } from "./useSupervisorsCardController"
import { EditCommissionModal } from "@/view/pages/Vendedores/SellerDetailsPage/components/EditCommissionModal"
import { useNavigate } from "react-router-dom"
import { UserRoleEnum } from "@/app/services/usersService/user"
import { useState } from "react"

export const SupervisorsCard = ({
  _id,
  name,
  commissionsV2,
  profileImageUrl,
  subordinates,
  members,
  membersTotal,
  transactions,
}: UserRole) => {
  const {
    user,
    isSupervisorNull,
  } = useSupervisorsCardController(_id)

  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false)

  const navigate = useNavigate()

  const handleCardClick = () => {
    navigate(`../../times/${_id}`, { state: { supervisorName: name } })
  }

  const filteredSubordinates = subordinates.filter(
    (subordinate) => subordinate._id !== null,
  )

  return (
    <Card
      className="group relative flex min-h-[580px] cursor-pointer flex-col"
      onClick={handleCardClick}
    >
      <CardHeader className="grow">
        {isSupervisorNull ? (
          <>
            <div className="mb-7 mt-2 flex items-center justify-between">
              <p className="text-lg font-semibold">Produção Supervisores</p>
            </div>
            <div className="space-y-4">
              <div className="w-full border-[1px] border-white02" />
              <div className="flex items-center justify-center gap-4">
                <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white px-3 py-2">
                  <p className="font-semibold">{transactions.count}</p>
                  <p className="text-xs font-medium text-black04">
                    Saques realizados
                  </p>
                </div>
                <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white px-3 py-2">
                  <p className="font-semibold">
                    {formatMoneyValue(transactions.total)}
                  </p>
                  <p className="text-xs font-medium text-black04">
                    Valor Movimentado
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">
                  {filteredSubordinates.length}{" "}
                  {filteredSubordinates.length === 1 ? "Líder" : "Lideres"}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage
                    src={
                      profileImageUrl
                        ? `${import.meta.env.VITE_CDN_URL}${profileImageUrl}`
                        : undefined
                    }
                  />
                  <AvatarFallback
                    className="text-lg"
                    style={{
                      backgroundColor: stringToColour(name || ""),
                    }}
                  >
                    {getNameInitial(name || "")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <p className="text-lg font-semibold">
                    {name ? formatName(name) : ""}
                  </p>
                  {(user?.role.includes(UserRoleEnum.SUPER_ADMIN) || user?.role.includes(UserRoleEnum.ADMIN)) && (
                    <button
                      type="button"
                      className="text-sm text-primary"
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsCommissionModalOpen(true)
                      }}
                    >
                      ajustar comissão
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-fit w-fit rounded-3xl bg-[#FDF3D0] px-2 py-1">
                  <p className="text-md font-semibold text-[#A68E38]">
                    Supervisor
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="w-full border-[1px] border-white02" />
              <div className="flex items-center justify-center gap-4">
                <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white px-3 py-2">
                  <p className="font-semibold">{transactions.count}</p>
                  <p className="text-xs font-medium text-black04">
                    Saques realizados
                  </p>
                </div>
                <div className="flex h-20 w-full flex-col items-center justify-center rounded-lg border-[1px] border-gray02 bg-white px-3 py-2">
                  <p className="font-semibold">
                    {formatMoneyValue(transactions.total)}
                  </p>
                  <p className="text-xs font-medium text-black04">
                    Valor Movimentado
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">
                  {filteredSubordinates.length}{" "}
                  {filteredSubordinates.length === 1 ? "Líder" : "Lideres"}
                </p>
              </div>
            </div>
          </>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <Goals
            currentValue={transactions.total}
            subordinates={subordinates.slice(0, 2).map((subordinate) => ({
              name: subordinate.name || "Sem líder",
              total: subordinate.transactions.total,
            }))}
            variant="subordinate"
          />
        </div>
        <div className="mt-4 w-full border-[1px] border-white02" />
      </CardContent>
      <CardFooter className="flex flex-col items-start">
        <p className="font-semibold">
          {membersTotal} {membersTotal === 1 ? "membro" : "membros"}
        </p>
        <div className="mt-4 flex flex-wrap">
          {members.length > 0 ? (
            <>
              {members.slice(0, 10).map((member, index) => (
                <Avatar
                  key={member._id}
                  className="h-8 w-8 border-2 border-white"
                  style={{ marginLeft: index > 0 ? "-6px" : "0" }}
                >
                  <AvatarImage src={member.profileImageUrl || ""} />
                  <AvatarFallback
                    style={{ backgroundColor: stringToColour(member.name) }}
                  >
                    {getNameInitial(member.name)}
                  </AvatarFallback>
                </Avatar>
              ))}
              {members.length > 10 && (
                <div className="ml-2 text-sm font-semibold">
                  +{members.length - 10}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-sm text-black04">
              <p />
            </div>
          )}
        </div>
      </CardFooter>
      <EditCommissionModal
        open={isCommissionModalOpen}
        onClose={() => setIsCommissionModalOpen(false)}
        sellerId={_id || ""}
        commissions={commissionsV2}
      />
    </Card>
  )
}
